import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { PhoneCall, Search, Zap, TrendingUp } from "lucide-react";

const METRIC_LABELS = [
  ["calls_due_today", "Due Today"], ["overdue_followups", "Overdue"], ["attempts_today", "Attempts"],
  ["connections_today", "Connections"], ["meaningful_today", "Meaningful"], ["appointments_today", "Appointments"],
  ["qualified_today", "Qualified"],
];

export default function DailyCallQueue() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [metrics, setMetrics] = useState({});
  const [q, setQ] = useState("");
  const [minScore, setMinScore] = useState("");
  const [due, setDue] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (minScore) params.min_highest_score = Number(minScore);
      if (due) params.due = due;
      const { data } = await api.get("/boi/queue", { params });
      let list = data.items;
      if (q) list = list.filter((r) => r.name.toLowerCase().includes(q.toLowerCase()));
      setItems(list); setTotal(data.total);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  }, [minScore, due, q]);
  useEffect(() => { load(); api.get("/boi/metrics").then(({ data }) => setMetrics(data)).catch(() => {}); }, [load]);

  const startCalling = async () => {
    try {
      const { data } = await api.get("/boi/queue/next");
      if (data.relationship_id) nav(`/call-workspace/${data.relationship_id}`);
      else toast.info("No eligible relationships in your queue.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="daily-call-queue-page">
      <PageHeader title="Daily Call Queue" subtitle="Deterministically prioritized relationships to work today — DNC-suppressed.">
        <Button onClick={startCalling} className="bg-navy hover:bg-atlantic" data-testid="start-calling-btn"><PhoneCall className="w-4 h-4 mr-1" />Start Calling</Button>
      </PageHeader>

      <div className="grid grid-cols-3 md:grid-cols-7 gap-2 mb-4" data-testid="queue-metrics">
        {METRIC_LABELS.map(([k, l]) => (
          <div key={k} className="bg-white border border-border rounded-sm p-3 text-center">
            <div className="font-serif text-2xl text-navy">{metrics[k] ?? 0}</div>
            <div className="text-xs uppercase text-mutedsea tracking-wide">{l}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative flex-1 max-w-sm"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-mutedsea" /><Input className="pl-8" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="queue-search" /></div>
        <select className="h-9 border border-border rounded-sm px-2 text-sm" value={minScore} onChange={(e) => setMinScore(e.target.value)} data-testid="queue-score-filter">
          <option value="">Any Score</option><option value="75">Score ≥ 75</option><option value="60">Score ≥ 60</option>
        </select>
        <select className="h-9 border border-border rounded-sm px-2 text-sm" value={due} onChange={(e) => setDue(e.target.value)} data-testid="queue-due-filter">
          <option value="">All</option><option value="today">Due Today</option><option value="overdue">Overdue</option>
        </select>
        <span className="text-xs text-mutedsea ml-auto">{total} in queue</span>
      </div>

      <div className="bg-white border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-cloud border-b border-border">{["Priority", "Company", "Best Next Opportunity", "Top Score", "Best Next Action", "Last Contact", "Next Follow-Up", "Why"].map((h) => <th key={h} className="text-left px-3 py-2 text-xs uppercase text-navy font-semibold">{h}</th>)}</tr></thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} onClick={() => nav(`/call-workspace/${r.id}`)} className="border-b border-border last:border-0 hover:bg-ice cursor-pointer" data-testid={`queue-row-${r.id}`}>
                <td className="px-3 py-2"><span className="font-serif text-lg text-navy inline-flex items-center gap-1"><Zap className="w-3.5 h-3.5 text-seaamber" />{r.priority_score}</span></td>
                <td className="px-3 py-2 font-medium text-navy">{r.name}</td>
                <td className="px-3 py-2 text-xs">{r.best_next_opportunity?.business_line_name || "—"}{r.best_next_opportunity ? ` (${r.best_next_opportunity.score})` : ""}</td>
                <td className="px-3 py-2"><span className="font-serif text-navy">{r.highest_score ?? 0}</span></td>
                <td className="px-3 py-2 text-xs text-atlantic">{r.best_next_action_label || "—"}</td>
                <td className="px-3 py-2 text-xs text-mutedsea">{r.last_contact_days != null ? `${r.last_contact_days}d ago` : "never"}</td>
                <td className="px-3 py-2 text-xs text-mutedsea">{r.next_follow_up || "—"}</td>
                <td className="px-3 py-2 text-xs text-mutedsea max-w-xs">{(r.priority_why || []).slice(0, 2).join(" · ")}</td>
              </tr>
            ))}
            {!loading && items.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-mutedsea text-sm" data-testid="queue-empty">No relationships to call. Create or promote relationships to build your queue.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
